import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function GLAccountsSummary() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchGLSummary();
  }, []);

  const fetchGLSummary = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/accounting/gl-accounts-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setData(response.data.data);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching GL accounts summary:', err);
      setError(err.response?.data?.error?.message || 'Failed to load GL accounts summary');
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value || 0);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(num);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value || 0);
  };

  const getAccountTypeColor = (type) => {
    const colors = {
      'asset': '#2196f3',
      'liability': '#ff9800',
      'equity': '#9c27b0',
      'revenue': '#4caf50',
      'expense': '#f44336',
      'cogs': '#795548',
      'other': '#757575'
    };
    return colors[String(type || '').toLowerCase()] || colors['other'];
  };

  // Natural (normal-side) balances: assets/expenses/COGS are debit-normal,
  // liabilities/equity/revenue credit-normal. The API's net_balance is
  // debits-credits, so credit-normal types read as huge negatives and the old
  // raw-sign green/red painted a healthy CoA as alarming losses (a CFO reads
  // "$80M revenue", never "-$80M net balance"). Red is reserved for genuinely
  // abnormal (contra-side) balances.
  const DEBIT_NORMAL = new Set(['asset', 'expense', 'cogs']);
  const isDebitNormal = (type) => DEBIT_NORMAL.has(String(type || '').toLowerCase());
  const naturalBalance = (type, netBalance) => {
    const net = parseFloat(netBalance || 0);
    return isDebitNormal(type) ? net : -net;
  };

  const getAccountTypeIcon = (type) => {
    const netBalance = parseFloat(data?.by_account_type?.find(t => t.account_type === type)?.net_balance || 0);
    return netBalance >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info">
        No GL account data available
      </Alert>
    );
  }

  return (
    <Box>
      {/* Account Type Summary Cards */}
      <Box mb={4}>
        <Typography variant="h6" fontWeight="600" gutterBottom>
          Chart of Accounts by Type
        </Typography>
        <Grid container spacing={2} mt={1}>
          {data.by_account_type && data.by_account_type.map((typeData, idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Paper
                sx={{
                  p: 2.5,
                  borderLeft: `4px solid ${getAccountTypeColor(typeData.account_type)}`,
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 3
                  }
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {typeData.account_type}
                    </Typography>
                    <Typography variant="h6" fontWeight="700">
                      {typeData.total_accounts} Account{typeData.total_accounts !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  <AccountBalanceIcon
                    sx={{
                      fontSize: 32,
                      color: getAccountTypeColor(typeData.account_type),
                      opacity: 0.7
                    }}
                  />
                </Box>

                <Box mt={2}>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" color="text.secondary">
                      Total Debits
                    </Typography>
                    <Typography variant="body2" fontWeight="600">
                      {formatCurrency(typeData.total_debits)}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" color="text.secondary">
                      Total Credits
                    </Typography>
                    <Typography variant="body2" fontWeight="600">
                      {formatCurrency(typeData.total_credits)}
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    mt={1}
                    pt={1}
                    sx={{ borderTop: '1px solid rgba(0,0,0,0.1)' }}
                  >
                    <Typography variant="body2" fontWeight="700">
                      Balance ({isDebitNormal(typeData.account_type) ? 'Dr' : 'Cr'} normal)
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="700"
                      color={naturalBalance(typeData.account_type, typeData.net_balance) >= 0 ? 'text.primary' : 'error.main'}
                    >
                      {formatCurrency(naturalBalance(typeData.account_type, typeData.net_balance))}
                      {naturalBalance(typeData.account_type, typeData.net_balance) < 0 ? ' (contra)' : ''}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Top Active Accounts */}
      <Box>
        <Typography variant="h6" fontWeight="600" gutterBottom>
          Top 20 Most Active Accounts
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Accounts with the highest transaction volume
        </Typography>

        {/* Group by account type */}
        {data.by_account_type && data.by_account_type.map((typeData, idx) => {
          const accountsOfType = data.top_active_accounts?.filter(
            acc => acc.account_type === typeData.account_type
          ) || [];

          if (accountsOfType.length === 0) return null;

          return (
            <Accordion key={idx} defaultExpanded={idx === 0}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  borderLeft: `4px solid ${getAccountTypeColor(typeData.account_type)}`
                }}
              >
                <Box display="flex" alignItems="center" gap={2} width="100%">
                  <Typography variant="subtitle1" fontWeight="600">
                    {typeData.account_type}
                  </Typography>
                  <Chip
                    label={`${accountsOfType.length} account${accountsOfType.length !== 1 ? 's' : ''}`}
                    size="small"
                    sx={{
                      backgroundColor: getAccountTypeColor(typeData.account_type),
                      color: 'white'
                    }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Account Code</TableCell>
                        <TableCell>Account Name</TableCell>
                        <TableCell align="right">Transactions</TableCell>
                        <TableCell align="right">Total Debits</TableCell>
                        <TableCell align="right">Total Credits</TableCell>
                        <TableCell align="right">Balance (natural sign)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {accountsOfType.map((account) => (
                        <TableRow key={account.account_id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="600">
                              {account.account_code}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {account.account_name}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={formatNumber(account.total_lines)}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="600">
                              {formatCurrency(account.total_debits)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="600">
                              {formatCurrency(account.total_credits)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              fontWeight="700"
                              color={naturalBalance(account.account_type, account.net_balance) >= 0 ? 'text.primary' : 'error.main'}
                            >
                              {formatCurrency(naturalBalance(account.account_type, account.net_balance))}
                              {naturalBalance(account.account_type, account.net_balance) < 0 ? ' (contra)' : ''}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Box>
  );
}
